<script setup>
// [TODO] This componet is too big and bulky to be in the same file, we can consider splitting this into multiple
// composables and components, useVirtualChatList, useChatlistFilters
import {
  ref,
  unref,
  provide,
  computed,
  watch,
  onMounted,
  defineEmits,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store.js';

// [VITE] [TODO] We are using vue-virtual-scroll for now, since that seemed the simplest way to migrate
// from the current one. But we should consider using tanstack virtual in the future
// https://tanstack.com/virtual/latest/docs/framework/vue/examples/variable
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
import ChatListHeader from './ChatListHeader.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import SaveCustomView from 'next/filter/SaveCustomView.vue';
import ChatTypeTabs from './widgets/ChatTypeTabs.vue';
import ConversationItem from './ConversationItem.vue';
import MessagePreview from './widgets/conversation/MessagePreview.vue';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import IntersectionObserver from './IntersectionObserver.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions';
import { useFilter } from 'shared/composables/useFilter';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';
import { useEmitter } from 'dashboard/composables/emitter';
import { useEventListener } from '@vueuse/core';
import ContactAPI from 'dashboard/api/contacts';
import SearchAPI from 'dashboard/api/search';
import ConversationAPI from 'dashboard/api/inbox/conversation';

import { emitter } from 'shared/helpers/mitt';

import wootConstants from 'dashboard/constants/globals';
import advancedFilterOptions from './widgets/conversation/advancedFilterItems';
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import { conversationListPageURL, frontendURL } from '../helper/URLHelper';
import {
  isOnMentionsView,
  isOnUnattendedView,
} from '../store/modules/conversations/helpers/actionHelpers';
import {
  getUserPermissions,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper.js';
import { matchesFilters } from '../store/modules/conversations/helpers/filterHelpers';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';
import { conversationUrl } from '../helper/URLHelper';

import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

const props = defineProps({
  conversationInbox: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  showConversationList: { default: true, type: Boolean },
  isOnExpandedLayout: { default: false, type: Boolean },
});

const emit = defineEmits(['conversationLoad']);
const { uiSettings, updateUISettings } = useUISettings();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const conversationListRef = ref(null);
const conversationDynamicScroller = ref(null);

provide('contextMenuElementTarget', conversationDynamicScroller);

const activeAssigneeTab = ref(wootConstants.ASSIGNEE_TYPE.ME);
const activeStatus = ref(wootConstants.STATUS_TYPE.OPEN);
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC);
const showAdvancedFilters = ref(false);
// chatsOnView is to store the chats that are currently visible on the screen,
// which mirrors the conversationList.
const chatsOnView = ref([]);
const foldersQuery = ref({});
const showAddFoldersModal = ref(false);
const showDeleteFoldersModal = ref(false);
const isContextMenuOpen = ref(false);
const appliedFilter = ref([]);
const conversationSearch = ref('');
const contactSearchResults = ref([]);
const searchedConversationResults = ref([]);
const isSearchingContacts = ref(false);
const contactSearchFailed = ref(false);
const ASSIGNEE_TAB_HIDDEN_KEY = 'hidden_conversation_assignee_tab_keys';
const ASSIGNEE_TAB_CONTEXT_MENU_WIDTH = 220;
const assigneeTabContextMenuVisible = ref(false);
const assigneeTabContextMenuX = ref(0);
const assigneeTabContextMenuY = ref(0);
const assigneeTabContextMenuItem = ref(null);
let contactSearchDebounceTimer = null;
let contactSearchRunId = 0;
const advancedFilterTypes = ref(
  advancedFilterOptions.map(filter => ({
    ...filter,
    attributeName: t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
  }))
);

const currentUser = useMapGetter('getCurrentUser');
const chatLists = useMapGetter('getFilteredConversations');
const mineChatsList = useMapGetter('getMineChats');
const allChatList = useMapGetter('getAllStatusChats');
const unAssignedChatsList = useMapGetter('getUnAssignedChats');
const chatListLoading = useMapGetter('getChatListLoadingStatus');
const activeInbox = useMapGetter('getSelectedInbox');
const conversationStats = useMapGetter('conversationStats/getStats');
const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const folders = useMapGetter('customViews/getConversationCustomViews');
const agentList = useMapGetter('agents/getAgents');
const teamsList = useMapGetter('teams/getTeams');
const inboxesList = useMapGetter('inboxes/getInboxes');
const campaigns = useMapGetter('campaigns/getAllCampaigns');
const labels = useMapGetter('labels/getLabels');
const currentAccountId = useMapGetter('getCurrentAccountId');
const selectedChat = useMapGetter('getSelectedChat');
// We can't useFunctionGetter here since it needs to be called on setup?
const getTeamFn = useMapGetter('teams/getTeam');

useChatListKeyboardEvents(conversationListRef);
const {
  selectedConversations,
  selectedInboxes,
  selectConversation,
  deSelectConversation,
  selectAllConversations,
  resetBulkActions,
  isConversationSelected,
  onAssignAgent: onAssignAgentBulk,
  onAssignLabels: onAssignLabelsBulk,
  onAssignTeamsForBulk: onAssignTeamsForBulk,
  onUpdateConversations,
} = useBulkActions();

const {
  initializeStatusAndAssigneeFilterToModal,
  initializeInboxTeamAndLabelFilterToModal,
} = useFilter({
  filteri18nKey: 'FILTER',
  attributeModel: 'conversation_attribute',
});

// computed
const intersectionObserverOptions = computed(() => {
  return {
    root: conversationListRef.value,
    rootMargin: '100px 0px 100px 0px',
  };
});

const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length !== 0;
});

const activeFolder = computed(() => {
  if (props.foldersId) {
    const activeView = folders.value.filter(
      view => view.id === Number(props.foldersId)
    );
    const [firstValue] = activeView;
    return firstValue;
  }
  return undefined;
});

const activeFolderName = computed(() => {
  return activeFolder.value?.name;
});

const hasActiveFolders = computed(() => {
  return Boolean(activeFolder.value && props.foldersId !== 0);
});

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return hasAppliedFilters.value || hasActiveFolders.value;
});

const shouldShowAssigneeTabs = computed(() => {
  return !hasAppliedFiltersOrActiveFolders.value;
});

const currentUserDetails = computed(() => {
  const { id, name } = currentUser.value;
  return { id, name };
});

const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

const assigneeTabItems = computed(() => {
  const items = filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  ).map(({ key, count: countKey }) => ({
    key,
    name: t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
    count: conversationStats.value[countKey] || 0,
  }));

  return isSharedQueueInbox.value
    ? items.filter(item => item.key === wootConstants.ASSIGNEE_TYPE.ALL)
    : items;
});

const hiddenAssigneeTabKeys = computed(() => {
  const value = uiSettings.value?.[ASSIGNEE_TAB_HIDDEN_KEY];
  return Array.isArray(value)
    ? [...new Set(value.map(item => String(item)).filter(Boolean))]
    : [];
});

const assigneeTabContextMenuStyle = computed(() => ({
  left: `${assigneeTabContextMenuX.value}px`,
  top: `${assigneeTabContextMenuY.value}px`,
}));

const assigneeTabContextMenuItemName = computed(
  () => assigneeTabContextMenuItem.value?.name || ''
);

const showAssigneeInConversationCard = computed(() => {
  return (
    hasAppliedFiltersOrActiveFolders.value ||
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ALL
  );
});

const currentPageFilterKey = computed(() => {
  return hasAppliedFiltersOrActiveFolders.value
    ? 'appliedFilters'
    : activeAssigneeTab.value;
});

const resolveConversationInboxId = rawInboxId => {
  const numericInboxId = Number(rawInboxId || 0);
  if (!numericInboxId) return 0;

  const directMatch = inboxesList.value.find(
    record => Number(record.id) === numericInboxId
  );
  if (directMatch?.id) {
    return Number(directMatch.id);
  }

  const channelMatch = inboxesList.value.find(
    record => Number(record.channel_id) === numericInboxId
  );
  if (channelMatch?.id) {
    return Number(channelMatch.id);
  }

  return numericInboxId;
};

const resolvedConversationInbox = computed(() =>
  resolveConversationInboxId(props.conversationInbox)
);

const inbox = useFunctionGetter('inboxes/getInbox', resolvedConversationInbox);
const isSharedQueueInbox = computed(
  () => inbox.value?.queue_mode === 'shared_queue'
);
const currentPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  activeAssigneeTab
);
const currentFiltersPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  currentPageFilterKey
);
const hasCurrentPageEndReached = useFunctionGetter(
  'conversationPage/getHasEndReached',
  currentPageFilterKey
);

const conversationCustomAttributes = useFunctionGetter(
  'attributes/getAttributesByModel',
  'conversation_attribute'
);

const activeAssigneeTabCount = computed(() => {
  const activeTabItem = assigneeTabItems.value.find(
    item => item.key === activeAssigneeTab.value
  );
  return activeTabItem ? activeTabItem.count : 0;
});

const conversationListPagination = computed(() => {
  const conversationsPerPage = 25;
  const hasChatsOnView =
    chatsOnView.value &&
    Array.isArray(chatsOnView.value) &&
    !chatsOnView.value.length;
  const isNoFiltersOrFoldersAndChatListNotEmpty =
    !hasAppliedFiltersOrActiveFolders.value && hasChatsOnView;
  const isUnderPerPage =
    chatsOnView.value.length < conversationsPerPage &&
    activeAssigneeTabCount.value < conversationsPerPage &&
    activeAssigneeTabCount.value > chatsOnView.value.length;

  if (isNoFiltersOrFoldersAndChatListNotEmpty && isUnderPerPage) {
    return 1;
  }

  return currentPage.value + 1;
});

const conversationFilters = computed(() => {
  return {
    inboxId: resolvedConversationInbox.value || undefined,
    assigneeType: activeAssigneeTab.value,
    status: activeStatus.value,
    sortBy: activeSortBy.value,
    page: conversationListPagination.value,
    labels: props.label ? [props.label] : undefined,
    teamId: props.teamId || undefined,
    conversationType: props.conversationType || undefined,
  };
});

const activeTeam = computed(() => {
  if (props.teamId) {
    return getTeamFn.value(props.teamId);
  }
  return {};
});

const pageTitle = computed(() => {
  if (hasAppliedFilters.value) {
    return t('CHAT_LIST.TAB_HEADING');
  }
  if (inbox.value.name) {
    return inbox.value.name;
  }
  if (activeTeam.value.name) {
    return activeTeam.value.name;
  }
  if (props.label) {
    return `#${props.label}`;
  }
  if (props.conversationType === 'mention') {
    return t('CHAT_LIST.MENTION_HEADING');
  }
  if (props.conversationType === 'participating') {
    return t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
  }
  if (props.conversationType === 'unattended') {
    return t('CHAT_LIST.UNATTENDED_HEADING');
  }
  if (hasActiveFolders.value) {
    return activeFolder.value.name;
  }
  return t('CHAT_LIST.TAB_HEADING');
});

const conversationList = computed(() => {
  let localConversationList = [];

  if (!hasAppliedFiltersOrActiveFolders.value) {
    const filters = conversationFilters.value;
    if (activeAssigneeTab.value === 'me') {
      localConversationList = [...mineChatsList.value(filters)];
    } else if (activeAssigneeTab.value === 'unassigned') {
      localConversationList = [...unAssignedChatsList.value(filters)];
    } else {
      localConversationList = [...allChatList.value(filters)];
    }
  } else {
    localConversationList = [...chatLists.value];
  }

  if (activeFolder.value) {
    const { payload } = activeFolder.value.query;
    localConversationList = localConversationList.filter(conversation => {
      return matchesFilters(conversation, payload);
    });
  }

  return localConversationList;
});

const visibleAssigneeTabItems = computed(() => {
  const displayItems = assigneeTabItems.value.map(item => {
    if (item.key !== activeAssigneeTab.value) return item;

    return {
      ...item,
      count: conversationList.value.length,
    };
  });

  if (isSharedQueueInbox.value) return displayItems;

  const visibleItems = displayItems.filter(
    item => !hiddenAssigneeTabKeys.value.includes(item.key)
  );
  return visibleItems.length ? visibleItems : displayItems;
});

const conversationStatsDisplay = computed(() => {
  const stats = {
    mine_count: conversationStats.value?.mine_count || 0,
    assigned_count: conversationStats.value?.assigned_count || 0,
    unassigned_count: conversationStats.value?.unassigned_count || 0,
    all_count: conversationStats.value?.all_count || 0,
  };

  const visibleCount = conversationList.value.length;

  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ME) {
    stats.mine_count = visibleCount;
  } else if (
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.UNASSIGNED
  ) {
    stats.unassigned_count = visibleCount;
  } else {
    stats.all_count = visibleCount;
  }

  return stats;
});

const normalizeSearchText = value => {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
};

const normalizeDigits = value => String(value || '').replace(/\D/g, '');

const stripPhonePrefixes = digits => {
  let value = normalizeDigits(digits);
  if (value.startsWith('55') && value.length > 11) {
    value = value.slice(2);
  }
  return value;
};

const normalizeBrazilMobileDigits = digits => {
  const value = stripPhonePrefixes(digits);
  if (value.length === 11 && value[2] === '9') {
    return `${value.slice(0, 2)}${value.slice(3)}`;
  }
  return value;
};

const buildPhoneDigitVariants = digits => {
  const normalized = normalizeDigits(digits);
  if (!normalized) return [];

  const variants = new Set();
  const addVariant = value => {
    const cleanValue = normalizeDigits(value);
    if (cleanValue.length >= 4) variants.add(cleanValue);
  };

  const stripped = stripPhonePrefixes(normalized);
  const compact = normalizeBrazilMobileDigits(stripped);

  addVariant(normalized);
  addVariant(stripped);
  addVariant(compact);
  addVariant(`55${stripped}`);
  addVariant(`55${compact}`);

  if (compact.length >= 10) {
    const ddd = compact.slice(0, 2);
    const subscriber = compact.slice(2);

    addVariant(subscriber);

    if (subscriber.length === 8) {
      addVariant(`9${subscriber}`);
      addVariant(`${ddd}9${subscriber}`);
      addVariant(`55${ddd}9${subscriber}`);
    } else if (subscriber.length === 9 && subscriber.startsWith('9')) {
      const withoutNinth = subscriber.slice(1);
      addVariant(withoutNinth);
      addVariant(`${ddd}${withoutNinth}`);
      addVariant(`55${ddd}${withoutNinth}`);
    }
  } else if (compact.length === 9 && compact.startsWith('9')) {
    addVariant(compact.slice(1));
  }

  return [...variants];
};

const getPhoneMatchStrength = (a, b) => {
  const left = normalizeDigits(a);
  const right = normalizeDigits(b);
  if (!left || !right) return 0;

  const leftVariants = buildPhoneDigitVariants(left);
  const rightVariants = buildPhoneDigitVariants(right);

  for (let i = 0; i < leftVariants.length; i += 1) {
    for (let j = 0; j < rightVariants.length; j += 1) {
      const leftVariant = leftVariants[i];
      const rightVariant = rightVariants[j];

      if (leftVariant === rightVariant) {
        return 4;
      }

      const minLength = Math.min(leftVariant.length, rightVariant.length);
      if (
        minLength >= 8 &&
        (leftVariant.includes(rightVariant) || rightVariant.includes(leftVariant))
      ) {
        return 3;
      }

      const minSuffix = Math.min(8, leftVariant.length, rightVariant.length);
      if (minSuffix >= 8) {
        const leftSuffix = leftVariant.slice(-minSuffix);
        const rightSuffix = rightVariant.slice(-minSuffix);
        if (leftSuffix === rightSuffix) return 2;
      }
    }
  }

  return 0;
};

const hasCloseNumericMatch = (a, b) => {
  return getPhoneMatchStrength(a, b) > 0;
};

const boundedLevenshtein = (leftValue, rightValue, maxDistance = 2) => {
  const left = normalizeSearchText(leftValue);
  const right = normalizeSearchText(rightValue);
  if (!left || !right) return maxDistance + 1;
  if (left === right) return 0;
  if (Math.abs(left.length - right.length) > maxDistance) {
    return maxDistance + 1;
  }

  const previous = new Array(right.length + 1).fill(0);
  const current = new Array(right.length + 1).fill(0);

  for (let j = 0; j <= right.length; j += 1) previous[j] = j;

  for (let i = 1; i <= left.length; i += 1) {
    current[0] = i;
    let rowMin = current[0];

    for (let j = 1; j <= right.length; j += 1) {
      const substitutionCost = left[i - 1] === right[j - 1] ? 0 : 1;
      current[j] = Math.min(
        previous[j] + 1,
        current[j - 1] + 1,
        previous[j - 1] + substitutionCost
      );
      if (current[j] < rowMin) rowMin = current[j];
    }

    if (rowMin > maxDistance) return maxDistance + 1;
    for (let j = 0; j <= right.length; j += 1) previous[j] = current[j];
  }

  return previous[right.length];
};

const buildNumericSearchPlans = queryDigits => {
  const plans = [];
  const addPlan = (term, maxPage = 1, priority = 1) => {
    const value = normalizeDigits(term);
    if (!value || value.length < 4) return;
    plans.push({ term: value, maxPage, priority });
  };

  const stripped = stripPhonePrefixes(queryDigits);
  const numericVariants = buildPhoneDigitVariants(queryDigits);

  addPlan(queryDigits, 4, 6);
  addPlan(stripped, 4, 5);

  numericVariants.forEach(term => {
    let priority = 3;
    let maxPage = 2;

    if (term === normalizeDigits(queryDigits)) {
      priority = 6;
      maxPage = 4;
    } else if (term === stripped || term === normalizeBrazilMobileDigits(stripped)) {
      priority = 5;
      maxPage = 4;
    } else if (term.length >= 10) {
      priority = 4;
      maxPage = 3;
    } else if (term.length >= 8) {
      priority = 3;
      maxPage = 2;
    }

    addPlan(term, maxPage, priority);
  });

  const deduped = new Map();
  plans.forEach(plan => {
    const key = plan.term;
    const existing = deduped.get(key);
    if (!existing) {
      deduped.set(key, plan);
      return;
    }

    deduped.set(key, {
      term: key,
      maxPage: Math.max(existing.maxPage, plan.maxPage),
      priority: Math.max(existing.priority, plan.priority),
    });
  });

  return [...deduped.values()]
    .sort((a, b) => b.priority - a.priority || b.term.length - a.term.length)
    .slice(0, 12);
};

const buildTextSearchPlans = query => {
  const plans = [];
  const addPlan = (term, maxPage = 1, priority = 1) => {
    const value = String(term || '').trim();
    if (!value || value.length < 2) return;
    plans.push({ term: value, maxPage, priority });
  };

  const original = String(query || '').trim();
  const normalized = normalizeSearchText(original);
  const tokens = normalized.split(/\s+/).filter(Boolean);

  addPlan(original, 3, 4);
  if (normalized !== original) addPlan(normalized, 3, 3);

  tokens.forEach((token, index) => {
    addPlan(token, 2, index === 0 ? 3 : 2);
    if (token.length >= 5) addPlan(token.slice(0, token.length - 1), 1, 1);
  });

  if (tokens.length >= 2) {
    addPlan(`${tokens[0]} ${tokens[1]}`, 2, 3);
    addPlan(`${tokens[0]} ${tokens[tokens.length - 1]}`, 2, 2);
  }

  const deduped = [...new Set(plans.map(plan => `${plan.term}::${plan.maxPage}`))];
  return deduped
    .map(value => {
      const [term, maxPageString] = value.split('::');
      return { term, maxPage: Number(maxPageString || 1), priority: term.length };
    })
    .sort((a, b) => b.priority - a.priority)
    .slice(0, 10);
};

const getContactSearchableNumericValues = contact => {
  const contactInboxes = Array.isArray(contact?.contact_inboxes)
    ? contact.contact_inboxes
    : [];
  const extraValues = contactInboxes.map(inbox => inbox?.source_id);
  return [contact?.phone_number, contact?.identifier, ...extraValues].filter(
    Boolean
  );
};

const normalizedConversationSearch = computed(() =>
  normalizeSearchText(conversationSearch.value)
);

const hasConversationSearch = computed(
  () => normalizedConversationSearch.value.length > 0
);

const getConversationSearchHaystack = conversation => {
  const sender = conversation?.meta?.sender || {};
  const lastMessage = conversation?.messages?.[0] || {};
  const values = [
    conversation?.id,
    sender?.name,
    sender?.phone_number,
    sender?.identifier,
    conversation?.contact_inbox?.source_id,
    lastMessage?.content,
    conversation?.additional_attributes?.mail_subject,
  ];
  return normalizeSearchText(values.filter(Boolean).join(' '));
};

const matchesConversationSearch = conversation => {
  if (!hasConversationSearch.value) return true;

  const textMatch = getConversationSearchHaystack(conversation).includes(
    normalizedConversationSearch.value
  );
  if (textMatch) return true;

  const queryDigits = normalizeDigits(conversationSearch.value);
  if (!queryDigits) return false;

  const sender = conversation?.meta?.sender || {};
  const valuesToCheck = [
    sender?.phone_number,
    sender?.identifier,
    conversation?.contact_inbox?.source_id,
  ];
  return valuesToCheck.some(value => hasCloseNumericMatch(value, queryDigits));
};

const filteredConversationList = computed(() => {
  if (!hasConversationSearch.value) return conversationList.value;

  return conversationList.value.filter(matchesConversationSearch);
});

const showConversationSearchNoResults = computed(() => {
  return (
    hasConversationSearch.value &&
    !chatListLoading.value &&
    filteredConversationList.value.length === 0 &&
    !isSearchingContacts.value &&
    searchedConversationResults.value.length === 0
  );
});

const hasContactSearchResults = computed(
  () => searchedConversationResults.value.length > 0
);

const showConversationEmptyState = computed(() => {
  if (chatListLoading.value) return false;
  if (filteredConversationList.value.length > 0) return false;
  if (hasConversationSearch.value && isSearchingContacts.value) return false;
  if (hasConversationSearch.value && hasContactSearchResults.value) return false;
  return true;
});

const showEndOfListMessage = computed(() => {
  return (
    !hasConversationSearch.value &&
    conversationList.value.length &&
    hasCurrentPageEndReached.value &&
    !chatListLoading.value
  );
});

const allConversationsSelected = computed(() => {
  return (
    conversationList.value.length === selectedConversations.value.length &&
    conversationList.value.every(el =>
      selectedConversations.value.includes(el.id)
    )
  );
});

const uniqueInboxes = computed(() => {
  return [...new Set(selectedInboxes.value)];
});

const lastBulkSelectionAnchorId = ref(null);

// ---------------------- Methods -----------------------
function setFiltersFromUISettings() {
  const { conversations_filter_by: filterBy = {} } = uiSettings.value;
  const { status, order_by: orderBy } = filterBy;
  activeStatus.value = status || wootConstants.STATUS_TYPE.OPEN;
  activeSortBy.value = Object.values(wootConstants.SORT_BY_TYPE).includes(
    orderBy
  )
    ? orderBy
    : wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;
}

function emitConversationLoaded() {
  emit('conversationLoad');
  // [VITE] removing this since the library has changed
  // nextTick(() => {
  //   // Addressing a known issue in the virtual list library where dynamically added items
  //   // might not render correctly. This workaround involves a slight manual adjustment
  //   // to the scroll position, triggering the list to refresh its rendering.
  //   const virtualList = conversationListRef.value;
  //   const scrollToOffset = virtualList?.scrollToOffset;
  //   const currentOffset = virtualList?.getOffset() || 0;
  //   if (scrollToOffset) {
  //     scrollToOffset(currentOffset + 1);
  //   }
  // });
}

function fetchFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(payload),
      page,
    })
    .then(emitConversationLoaded);

  showAdvancedFilters.value = false;
}

function fetchSavedFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: payload,
      page,
    })
    .then(emitConversationLoaded);
}

function onApplyFilter(payload) {
  payload = useSnakeCase(payload);
  resetBulkActions();
  foldersQuery.value = filterQueryGenerator(payload);
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  fetchFilteredConversations(payload);
}

function closeAdvanceFiltersModal() {
  showAdvancedFilters.value = false;
  appliedFilter.value = [];
}

function onUpdateSavedFilter(payload, folderName) {
  const transformedPayload = useSnakeCase(payload);
  const payloadData = {
    ...unref(activeFolder),
    name: unref(folderName),
    query: filterQueryGenerator(transformedPayload),
  };
  store.dispatch('customViews/update', payloadData);
  closeAdvanceFiltersModal();
}

function onClickOpenAddFoldersModal() {
  showAddFoldersModal.value = true;
}

function onCloseAddFoldersModal() {
  showAddFoldersModal.value = false;
}

function onClickOpenDeleteFoldersModal() {
  showDeleteFoldersModal.value = true;
}

function onCloseDeleteFoldersModal() {
  showDeleteFoldersModal.value = false;
}

function setParamsForEditFolderModal() {
  // Here we are setting the params for edit folder modal to show the existing values.

  // For agent, team, inboxes,and campaigns we get only the id's from the query.
  // So we are mapping the id's to the actual values.

  // For labels we get the name of the label from the query.
  // If we delete the label from the label list then we will not be able to show the label name.

  // For custom attributes we get only attribute key.
  // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
  return {
    agents: agentList.value,
    teams: teamsList.value,
    inboxes: inboxesList.value,
    labels: labels.value,
    campaigns: campaigns.value,
    languages: languages,
    countries: countries,
    priority: [
      { id: 'low', name: t('CONVERSATION.PRIORITY.OPTIONS.LOW') },
      { id: 'medium', name: t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM') },
      { id: 'high', name: t('CONVERSATION.PRIORITY.OPTIONS.HIGH') },
      { id: 'urgent', name: t('CONVERSATION.PRIORITY.OPTIONS.URGENT') },
    ],
    filterTypes: advancedFilterTypes.value,
    allCustomAttributes: conversationCustomAttributes.value,
  };
}

function initializeExistingFilterToModal() {
  const statusFilter = initializeStatusAndAssigneeFilterToModal(
    activeStatus.value,
    currentUserDetails.value,
    activeAssigneeTab.value
  );
  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  if (statusFilter) {
    appliedFilter.value = [...appliedFilter.value, useCamelCase(statusFilter)];
  }

  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  const otherFilters = initializeInboxTeamAndLabelFilterToModal(
    resolvedConversationInbox.value,
    inbox.value,
    props.teamId,
    activeTeam.value,
    props.label
  ).map(useCamelCase);

  appliedFilter.value = [...appliedFilter.value, ...otherFilters];
}

function initializeFolderToFilterModal(newActiveFolder) {
  // Here we are setting the params for edit folder modal.
  //  To show the existing values. when we click on edit folder button.

  // Here we get the query from the active folder.
  // And we are mapping the query to the actual values.
  // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
  const query = unref(newActiveFolder)?.query?.payload;
  if (!Array.isArray(query)) return;

  const newFilters = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditFolderModal()
        )
      : [];

    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [...appliedFilter.value, ...newFilters];
}

function initalizeAppliedFiltersToModal() {
  appliedFilter.value = [...appliedFilters.value];
}

function onToggleAdvanceFiltersModal() {
  if (showAdvancedFilters.value === true) {
    closeAdvanceFiltersModal();
    return;
  }

  if (!hasAppliedFilters.value && !hasActiveFolders.value) {
    initializeExistingFilterToModal();
  }
  if (hasActiveFolders.value) {
    initializeFolderToFilterModal(activeFolder.value);
  }
  if (hasAppliedFilters.value) {
    initalizeAppliedFiltersToModal();
  }

  showAdvancedFilters.value = true;
}

function fetchConversations() {
  store.dispatch('updateChatListFilters', conversationFilters.value);
  store.dispatch('fetchAllConversations').then(emitConversationLoaded);
}

function resetAndFetchData() {
  appliedFilter.value = [];
  resetBulkActions();
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store.dispatch('clearConversationFilters');
  if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  }
  if (props.foldersId) {
    return;
  }
  fetchConversations();
}

function loadMoreConversations() {
  if (hasCurrentPageEndReached.value || chatListLoading.value) {
    return;
  }

  if (!hasAppliedFiltersOrActiveFolders.value) {
    fetchConversations();
  } else if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  }
}

// Add a method to handle scroll events
function handleScroll() {
  const scroller = conversationDynamicScroller.value;
  if (scroller && scroller.hasScrollbar) {
    const { scrollTop, scrollHeight, clientHeight } = scroller.$el;
    if (scrollHeight - (scrollTop + clientHeight) < 100) {
      loadMoreConversations();
    }
  }
}

function updateAssigneeTab(selectedTab) {
  if (activeAssigneeTab.value !== selectedTab) {
    resetBulkActions();
    emitter.emit('clearSearchInput');
    activeAssigneeTab.value = selectedTab;
    if (!currentPage.value) {
      fetchConversations();
    }
  }
}

function closeAssigneeTabContextMenu() {
  assigneeTabContextMenuVisible.value = false;
  assigneeTabContextMenuItem.value = null;
}

function persistHiddenAssigneeTabs(keys = []) {
  updateUISettings({
    [ASSIGNEE_TAB_HIDDEN_KEY]: [...new Set(keys.map(key => String(key)))],
  });
}

function onAssigneeTabContextMenu({ event, item }) {
  if (isSharedQueueInbox.value) return;
  if (!event || !item?.key) return;
  event.preventDefault();

  const padding = 8;
  const menuHeight = hiddenAssigneeTabKeys.value.length ? 88 : 52;
  const maxX = window.innerWidth - ASSIGNEE_TAB_CONTEXT_MENU_WIDTH - padding;
  const maxY = window.innerHeight - menuHeight - padding;

  assigneeTabContextMenuX.value = Math.max(
    padding,
    Math.min(event.clientX, maxX)
  );
  assigneeTabContextMenuY.value = Math.max(
    padding,
    Math.min(event.clientY, maxY)
  );
  assigneeTabContextMenuItem.value = item;
  assigneeTabContextMenuVisible.value = true;
}

function hideSelectedAssigneeTab() {
  const selectedTabKey = assigneeTabContextMenuItem.value?.key;
  if (!selectedTabKey) return;

  if (visibleAssigneeTabItems.value.length <= 1) {
    useAlert('Pelo menos uma aba precisa ficar visivel.');
    closeAssigneeTabContextMenu();
    return;
  }

  const nextHiddenTabs = [...hiddenAssigneeTabKeys.value, selectedTabKey];
  persistHiddenAssigneeTabs(nextHiddenTabs);

  if (activeAssigneeTab.value === selectedTabKey) {
    const nextVisibleTab = assigneeTabItems.value.find(
      tab => !nextHiddenTabs.includes(tab.key)
    );
    if (nextVisibleTab) updateAssigneeTab(nextVisibleTab.key);
  }

  closeAssigneeTabContextMenu();
}

function showAllAssigneeTabs() {
  if (!hiddenAssigneeTabKeys.value.length) {
    closeAssigneeTabContextMenu();
    return;
  }
  persistHiddenAssigneeTabs([]);
  closeAssigneeTabContextMenu();
}

function onBasicFilterChange(value, type) {
  if (type === 'status') {
    activeStatus.value = value;
  } else {
    activeSortBy.value = value;
  }
  resetAndFetchData();
}

function openLastSavedItemInFolder() {
  const lastItemOfFolder = folders.value[folders.value.length - 1];
  const lastItemId = lastItemOfFolder.id;
  router.push({
    name: 'folder_conversations',
    params: { id: lastItemId },
  });
}

function openLastItemAfterDeleteInFolder() {
  if (folders.value.length > 0) {
    openLastSavedItemInFolder();
  } else {
    router.push({ name: 'home' });
    fetchConversations();
  }
}

function redirectToConversationList() {
  const {
    params: { accountId, inbox_id: inboxId, label, teamId },
    name,
  } = route;
  const normalizedInboxId = resolvedConversationInbox.value || inboxId;

  let conversationType = '';
  if (isOnMentionsView({ route: { name } })) {
    conversationType = 'mention';
  } else if (isOnUnattendedView({ route: { name } })) {
    conversationType = 'unattended';
  }
  router.push(
    conversationListPageURL({
      accountId,
      conversationType: conversationType,
      customViewId: props.foldersId,
      inboxId: normalizedInboxId,
      label,
      teamId,
    })
  );
}

async function assignPriority(priority, conversationId = null) {
  store.dispatch('setCurrentChatPriority', {
    priority,
    conversationId,
  });
  store.dispatch('assignPriority', { conversationId, priority }).then(() => {
    useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
      newValue: priority,
      from: 'Context menu',
    });
    useAlert(
      t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
        priority,
        conversationId,
      })
    );
  });
}

async function markAsUnread(conversationId) {
  try {
    await store.dispatch('markMessagesUnread', {
      id: conversationId,
    });
    redirectToConversationList();
  } catch (error) {
    // Ignore error
  }
}
async function markAsRead(conversationId) {
  try {
    await store.dispatch('markMessagesRead', {
      id: conversationId,
    });
  } catch (error) {
    // Ignore error
  }
}

async function onAssignTeam(team, conversationId = null) {
  const chatId = Number(
    Array.isArray(conversationId) ? conversationId[0] : conversationId
  );
  const shouldApplyBulk =
    Number.isFinite(chatId) &&
    selectedConversations.value.length > 1 &&
    selectedConversations.value.includes(chatId);

  if (shouldApplyBulk) {
    await onAssignTeamsForBulk(team);
    return;
  }

  try {
    await store.dispatch('assignTeam', {
      conversationId,
      teamId: team.id,
    });
    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL', {
        team: team.name,
        conversationId,
      })
    );
  } catch (error) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
  }
}

async function onAssignAgent(agent, conversationId = null) {
  const chatId = Number(
    Array.isArray(conversationId) ? conversationId[0] : conversationId
  );
  const shouldApplyBulk =
    Number.isFinite(chatId) &&
    selectedConversations.value.length > 1 &&
    selectedConversations.value.includes(chatId);

  if (shouldApplyBulk) {
    await onAssignAgentBulk(agent);
    return;
  }

  await onAssignAgentBulk(agent, conversationId);
}

async function onAssignLabels(payload, conversationId = null) {
  const chatId = Number(
    Array.isArray(conversationId) ? conversationId[0] : conversationId
  );
  const shouldApplyBulk =
    Number.isFinite(chatId) &&
    selectedConversations.value.length > 1 &&
    selectedConversations.value.includes(chatId);

  if (shouldApplyBulk) {
    await onAssignLabelsBulk(payload);
    return;
  }

  await onAssignLabelsBulk(payload, conversationId);
}

function toggleConversationStatus(
  conversationId,
  status,
  snoozedUntil,
  comportamento = null
) {
  const hasBulkSelection =
    selectedConversations.value.length > 1 &&
    selectedConversations.value.includes(conversationId);

  if (hasBulkSelection) {
    onUpdateConversations(status, snoozedUntil, comportamento);
    return;
  }

  store
    .dispatch('toggleStatus', {
      conversationId,
      status,
      snoozedUntil,
      comportamento,
    })
    .then(() => {
      useAlert(t('CONVERSATION.CHANGE_STATUS'));
    });
}

function allSelectedConversationsStatus(status) {
  if (!selectedConversations.value.length) return false;
  return selectedConversations.value.every(item => {
    return store.getters.getConversationById(item)?.status === status;
  });
}

function onContextMenuToggle(state) {
  isContextMenuOpen.value = state;
}

function toggleSelectAll(check) {
  selectAllConversations(check, filteredConversationList);
}

function selectConversationRange(anchorId, targetId, shouldSelect) {
  const chats = filteredConversationList.value;
  const anchorIndex = chats.findIndex(item => item.id === anchorId);
  const targetIndex = chats.findIndex(item => item.id === targetId);

  if (anchorIndex < 0 || targetIndex < 0) return false;

  const rangeStart = Math.min(anchorIndex, targetIndex);
  const rangeEnd = Math.max(anchorIndex, targetIndex);
  const range = chats.slice(rangeStart, rangeEnd + 1);

  range.forEach(chat => {
    const isSelected = isConversationSelected(chat.id);

    if (shouldSelect && !isSelected) {
      selectConversation(chat.id, chat.inbox_id);
    }

    if (!shouldSelect && isSelected) {
      deSelectConversation(chat.id, chat.inbox_id);
    }
  });

  return true;
}

function onSelectConversation(conversationId, inboxId, selectionMeta = {}) {
  const shouldSelect = selectionMeta.checked ?? true;
  const shiftPressed = !!selectionMeta.shiftKey;
  const firstVisibleConversationId = conversationList.value[0]?.id;
  const activeConversationId = selectedChat.value?.id;
  const anchorId =
    lastBulkSelectionAnchorId.value ||
    activeConversationId ||
    firstVisibleConversationId;

  if (
    shiftPressed &&
    anchorId &&
    anchorId !== conversationId &&
    selectConversationRange(anchorId, conversationId, shouldSelect)
  ) {
    lastBulkSelectionAnchorId.value = conversationId;
    return;
  }

  selectConversation(conversationId, inboxId);
  lastBulkSelectionAnchorId.value = conversationId;
}

function onDeSelectConversation(conversationId, inboxId, selectionMeta = {}) {
  const shouldSelect = selectionMeta.checked ?? false;
  const shiftPressed = !!selectionMeta.shiftKey;
  const firstVisibleConversationId = conversationList.value[0]?.id;
  const activeConversationId = selectedChat.value?.id;
  const anchorId =
    lastBulkSelectionAnchorId.value ||
    activeConversationId ||
    firstVisibleConversationId;

  if (
    shiftPressed &&
    anchorId &&
    anchorId !== conversationId &&
    selectConversationRange(anchorId, conversationId, shouldSelect)
  ) {
    lastBulkSelectionAnchorId.value = conversationId;
    return;
  }

  deSelectConversation(conversationId, inboxId);
  lastBulkSelectionAnchorId.value = conversationId;
}

function normalizeInboxColor(rawColor) {
  if (typeof rawColor !== 'string') return '';
  const trimmed = rawColor.trim();
  if (!trimmed) return '';
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  return /^#[0-9A-Fa-f]{6}$/.test(withHash) ? withHash.toUpperCase() : '';
}

function hexToRgba(hexColor, alpha = 0.1) {
  const normalized = normalizeInboxColor(hexColor);
  if (!normalized) return '';
  const color = normalized.slice(1);
  const r = parseInt(color.slice(0, 2), 16);
  const g = parseInt(color.slice(2, 4), 16);
  const b = parseInt(color.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function getSearchResultInbox(item) {
  const inboxId = item?.conversation?.inbox_id;
  if (!inboxId) return null;
  return (
    inboxesList.value.find(inbox => Number(inbox?.id) === Number(inboxId)) ||
    null
  );
}

function contactSearchInboxName(item) {
  const inbox = getSearchResultInbox(item);
  return inbox?.name || '';
}

function getSearchResultContactNumber(item) {
  const contact = item?.contact || {};
  const conversation = item?.conversation || {};

  return (
    contact?.phone_number ||
    contact?.identifier ||
    conversation?.contact_inbox?.source_id ||
    ''
  );
}

function isTruthySearchFlag(value) {
  return value === true || value === 'true' || value === 1 || value === '1';
}

function isInternalAgendaSearchContact(item) {
  const contact = item?.contact || {};
  const sender = item?.conversation?.meta?.sender || {};

  return (
    isTruthySearchFlag(contact?.custom_attributes?.internal_agenda) ||
    isTruthySearchFlag(sender?.custom_attributes?.internal_agenda)
  );
}

function contactSearchLabel(item) {
  const contact = item?.contact || {};
  const preferredNumber = getSearchResultContactNumber(item);

  if (isInternalAgendaSearchContact(item)) {
    return (
      contact?.name ||
      preferredNumber ||
      contact?.email ||
      `Contato #${contact?.id || ''}`
    );
  }

  return (
    preferredNumber ||
    contact?.name ||
    contact?.email ||
    `Contato #${contact?.id || ''}`
  );
}

function getSearchResultPreviewMessage(item) {
  return (
    item?.matchedMessage ||
    item?.conversation?.last_non_activity_message ||
    item?.conversation?.messages?.[0] ||
    null
  );
}

function getSearchResultTimestamp(item) {
  const matchedMessageTimestamp = Number(item?.matchedMessage?.created_at || 0);
  if (matchedMessageTimestamp) return matchedMessageTimestamp;

  return Number(
    item?.conversation?.last_activity_at || item?.conversation?.timestamp || 0
  );
}

function formatSearchResultTimestamp(timestamp) {
  const value = Number(timestamp || 0);
  if (!value) return '';

  const date = new Date(value * 1000);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  const sameYear = date.getFullYear() === now.getFullYear();

  if (sameDay) {
    return new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  if (sameYear) {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
    }).format(date);
  }

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  }).format(date);
}

function contactSearchDateLabel(item) {
  return formatSearchResultTimestamp(getSearchResultTimestamp(item));
}

function contactSearchItemStyle(item) {
  const inbox = getSearchResultInbox(item);
  const color = normalizeInboxColor(inbox?.color);
  if (!color) return {};

  return {
    borderInlineStart: `4px solid ${color}`,
    backgroundColor: hexToRgba(color, 0.12),
  };
}

function scoreContactMatch(contact, rawQuery, queryDigits) {
  const queryText = normalizeSearchText(rawQuery);
  const name = normalizeSearchText(contact?.name);
  const email = normalizeSearchText(contact?.email);
  const searchableDigits = getContactSearchableNumericValues(contact).map(value =>
    normalizeDigits(value)
  );

  let score = 0;

  if (queryText) {
    if (name && name.startsWith(queryText)) score += 120;
    else if (name && name.includes(queryText)) score += 80;
    if (email && email.includes(queryText)) score += 30;

    if (name && queryText.length >= 4) {
      const tokenDistances = name
        .split(/\s+/)
        .filter(Boolean)
        .map(token => boundedLevenshtein(token, queryText, 2));
      const fullNameDistance = boundedLevenshtein(name, queryText, 2);
      const bestDistance = Math.min(fullNameDistance, ...tokenDistances);
      if (bestDistance === 1) score += 55;
      else if (bestDistance === 2) score += 35;
    }
  }

  if (queryDigits) {
    searchableDigits.forEach(value => {
      const matchStrength = getPhoneMatchStrength(value, queryDigits);

      if (value === queryDigits || matchStrength === 4) {
        score += 220;
      } else if (value.startsWith(queryDigits)) {
        score += 170;
      } else if (value.includes(queryDigits) || queryDigits.includes(value)) {
        score += 130;
      } else if (matchStrength === 3) {
        score += 150;
      } else if (matchStrength === 2) {
        score += 120;
      }
    });
  }

  // keep deterministic ordering for ties
  score += Number(contact?.id || 0) / 1000000;
  return score;
}

function normalizeMessageSnippet(value) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
}

function buildMessageSearchPlans(query) {
  const plans = [];
  const addPlan = (term, maxPage = 1, fullHistory = false, priority = 1) => {
    const value = String(term || '').trim();
    if (!value || value.length < 2) return;
    plans.push({ term: value, maxPage, fullHistory, priority });
  };

  const rawQuery = String(query || '').trim();
  const normalized = normalizeSearchText(rawQuery);
  const shouldUseFullHistory = normalized.length >= 3;

  addPlan(rawQuery, 2, shouldUseFullHistory, 4);
  if (normalized && normalized !== rawQuery) {
    addPlan(normalized, 1, shouldUseFullHistory, 3);
  }

  const deduped = [
    ...new Set(
      plans.map(
        plan =>
          `${plan.term}::${plan.maxPage}::${plan.fullHistory}::${plan.priority}`
      )
    ),
  ];

  return deduped
    .map(value => {
      const [term, maxPageString, fullHistoryString, priorityString] =
        value.split('::');
      return {
        term,
        maxPage: Number(maxPageString || 1),
        fullHistory: fullHistoryString === 'true',
        priority: Number(priorityString || 1),
      };
    })
    .sort((a, b) => b.priority - a.priority)
    .slice(0, 2);
}

function scoreConversationSearchItem(item, rawQuery, queryDigits) {
  const queryText = normalizeSearchText(rawQuery);
  const snippet = normalizeSearchText(
    getSearchResultPreviewMessage(item)?.content || ''
  );
  let score = scoreContactMatch(item?.contact, rawQuery, queryDigits);

  if (queryText && snippet) {
    if (snippet.includes(queryText)) score += 200;
    else {
      const tokenMatches = queryText
        .split(/\s+/)
        .filter(Boolean)
        .filter(token => snippet.includes(token)).length;
      score += tokenMatches * 35;
    }
  }

  if (item?.matchSource === 'message') score += 32;
  if (item?.matchSource === 'both') score += 48;

  score += getSearchResultTimestamp(item) / 1000000000;
  return score;
}

async function searchMessagesForChatList(query, runId) {
  const trimmedQuery = String(query || '').trim();
  const queryPlans = buildMessageSearchPlans(trimmedQuery);
  if (!queryPlans.length) return [];

  const plannedRequests = queryPlans
    .flatMap(plan =>
      Array.from({ length: plan.maxPage }, (_, index) => ({
        term: plan.term,
        page: index + 1,
        fullHistory: plan.fullHistory,
      }))
    )
    .slice(0, 4);

  const responses = (
    await Promise.all(
      plannedRequests.map(({ term, page, fullHistory }) =>
        SearchAPI.messages({
          q: term,
          page,
          fullHistory,
          source: 'chat_list',
        }).catch(() => null)
      )
    )
  ).filter(Boolean);

  if (runId !== contactSearchRunId) return [];

  const mergedMessages = responses.flatMap(
    response => response?.data?.payload?.messages || []
  );

  const uniqueMessages = [
    ...new Map(mergedMessages.map(message => [message.id, message])).values(),
  ].sort(
    (left, right) =>
      Number(right?.created_at || 0) - Number(left?.created_at || 0)
  );

  const messageByConversationId = new Map();
  uniqueMessages.forEach(message => {
    const conversationId = Number(message?.conversation_id || 0);
    if (!conversationId) return;
    const current = messageByConversationId.get(conversationId);
    if (!current || Number(message?.created_at || 0) > Number(current?.created_at || 0)) {
      messageByConversationId.set(conversationId, message);
    }
  });

  const conversationIds = [...messageByConversationId.entries()]
    .sort(
      (left, right) =>
        Number(right?.[1]?.created_at || 0) - Number(left?.[1]?.created_at || 0)
    )
    .map(([conversationId]) => conversationId)
    .slice(0, 36);
  if (!conversationIds.length) return [];

  const conversationResponses = (
    await Promise.all(
      conversationIds.map(conversationId =>
        ConversationAPI.show(conversationId).catch(() => null)
      )
    )
  ).filter(Boolean);

  if (runId !== contactSearchRunId) return [];

  return conversationResponses
    .map(response => response?.data)
    .filter(Boolean)
    .map(conversation => {
      const message = messageByConversationId.get(Number(conversation?.id || 0));
      return {
        conversation,
        contact: conversation?.meta?.sender || {},
        matchedMessage: message || null,
        matchSource: 'message',
      };
    });
}

async function searchContactsForChatList(query) {
  const runId = ++contactSearchRunId;
  if (!query) {
    contactSearchResults.value = [];
    searchedConversationResults.value = [];
    isSearchingContacts.value = false;
    contactSearchFailed.value = false;
    return;
  }

  isSearchingContacts.value = true;
  contactSearchFailed.value = false;

  try {
    const trimmedQuery = String(query || '').trim();
    const queryDigits = normalizeDigits(trimmedQuery);
    const isNumericSearch = !!queryDigits;
    const queryPlans = queryDigits
      ? buildNumericSearchPlans(queryDigits)
      : buildTextSearchPlans(trimmedQuery);

    if (!queryPlans.length) {
      contactSearchResults.value = [];
      searchedConversationResults.value = [];
      return;
    }

    const plannedRequests = queryPlans
      .flatMap(plan =>
        Array.from({ length: plan.maxPage }, (_, index) => ({
          term: plan.term,
          page: index + 1,
        }))
      )
      .slice(0, queryDigits ? 28 : 18);

    const requests = plannedRequests.flatMap(({ term, page }) => {
      return [ContactAPI.search(term, page, 'name', '', true).catch(() => null)];
    });
    const messageSearchPromise = isNumericSearch
      ? Promise.resolve([])
      : searchMessagesForChatList(trimmedQuery, runId).catch(() => []);
    const [responses, messageSearchResults] = await Promise.all([
      Promise.all(requests),
      messageSearchPromise,
    ]);
    const validContactResponses = responses.filter(Boolean);

    // Ignore stale async results from older searches
    if (runId !== contactSearchRunId) return;

    const merged = validContactResponses.flatMap(
      response => response?.data?.payload || []
    );
    const deduped = [
      ...new Map(merged.map(contact => [contact.id, contact])).values(),
    ];
    contactSearchResults.value = deduped
      .filter(contact => {
        if (!queryDigits || queryDigits.length < 6) return true;
        const values = getContactSearchableNumericValues(contact);
        return values.some(value => hasCloseNumericMatch(value, queryDigits));
      })
      .sort(
        (a, b) =>
          scoreContactMatch(b, trimmedQuery, queryDigits) -
          scoreContactMatch(a, trimmedQuery, queryDigits)
      )
      .slice(0, 32);

    const conversationCollections = await Promise.all(
      contactSearchResults.value.map(async contact => {
        try {
          const response = await ContactAPI.getConversations(contact.id);
          const conversations = response?.data?.payload || [];
          return conversations.map(conversation => ({ conversation, contact }));
        } catch (error) {
          return [];
        }
      })
    );

    if (runId !== contactSearchRunId) return;

    const contactConversationResults = conversationCollections
      .flat()
      .map(item => ({
        ...item,
        matchSource: 'contact',
      }));

    const mergedByConversation = new Map();

    contactConversationResults.forEach(item => {
      const key = Number(item?.conversation?.id || 0);
      if (!key) return;
      mergedByConversation.set(key, item);
    });

    messageSearchResults.forEach(item => {
      const key = Number(item?.conversation?.id || 0);
      if (!key) return;
      const existing = mergedByConversation.get(key);
      if (existing) {
        mergedByConversation.set(key, {
          ...existing,
          matchedMessage: existing?.matchedMessage || item?.matchedMessage || null,
          matchSource: 'both',
        });
      } else {
        mergedByConversation.set(key, item);
      }
    });

    searchedConversationResults.value = [...mergedByConversation.values()]
      .sort((left, right) => {
        const leftScore = scoreConversationSearchItem(
          left,
          trimmedQuery,
          queryDigits
        );
        const rightScore = scoreConversationSearchItem(
          right,
          trimmedQuery,
          queryDigits
        );
        if (rightScore !== leftScore) return rightScore - leftScore;

        const leftMatchedTs = getSearchResultTimestamp(left);
        const rightMatchedTs = getSearchResultTimestamp(right);
        if (rightMatchedTs !== leftMatchedTs) {
          return rightMatchedTs - leftMatchedTs;
        }

        const leftTs = Number(
          left?.conversation?.last_activity_at || left?.conversation?.timestamp || 0
        );
        const rightTs = Number(
          right?.conversation?.last_activity_at || right?.conversation?.timestamp || 0
        );
        return rightTs - leftTs;
      })
      .slice(0, 45);
  } catch (error) {
    contactSearchResults.value = [];
    searchedConversationResults.value = [];
    contactSearchFailed.value = true;
  } finally {
    isSearchingContacts.value = false;
  }
}

function onContactSearchClick(item) {
  const conversation = item?.conversation;
  if (!conversation?.id) return;

  // Open the selected conversation directly, without inheriting the current
  // chat list filters (open/resolved/mentions/custom view), so search results
  // always navigate to the conversation history itself.
  const path = frontendURL(
    conversationUrl({
      accountId: currentAccountId.value,
      activeInbox: conversation?.inbox_id,
      id: conversation.id,
      conversationType: '',
      foldersId: 0,
      label: '',
      teamId: 0,
    }),
    item?.matchedMessage?.id ? { messageId: item.matchedMessage.id } : undefined
  );

  router.push(path);

  conversationSearch.value = '';
  searchedConversationResults.value = [];
  contactSearchResults.value = [];
  contactSearchFailed.value = false;
}

useEmitter('fetch_conversation_stats', () => {
  if (hasAppliedFiltersOrActiveFolders.value) return;
  store.dispatch('conversationStats/get', conversationFilters.value);
});

useEmitter('clearSearchInput', () => {
  conversationSearch.value = '';
  contactSearchResults.value = [];
  searchedConversationResults.value = [];
  isSearchingContacts.value = false;
  contactSearchFailed.value = false;
  if (contactSearchDebounceTimer) {
    clearTimeout(contactSearchDebounceTimer);
    contactSearchDebounceTimer = null;
  }
});

useEventListener(conversationDynamicScroller, 'scroll', handleScroll);
useEventListener(window, 'click', closeAssigneeTabContextMenu);
useEventListener(window, 'keydown', event => {
  if (event.key === 'Escape') closeAssigneeTabContextMenu();
});

onMounted(() => {
  store.dispatch('setChatListFilters', conversationFilters.value);
  setFiltersFromUISettings();
  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch('setChatSortFilter', activeSortBy.value);
  resetAndFetchData();
  if (hasActiveFolders.value) {
    store.dispatch('campaigns/get');
  }
});

const deleteConversationDialogRef = ref(null);
const conversationIdsToDelete = ref([]);
const deleteConversationTitle = computed(() => {
  const count = conversationIdsToDelete.value.length;

  if (count > 1) return `Excluir ${count} conversas`;

  return t('CONVERSATION.DELETE_CONVERSATION.TITLE', {
    conversationId: conversationIdsToDelete.value[0],
  });
});
const deleteConversationDescription = computed(() => {
  if (conversationIdsToDelete.value.length > 1) {
    return `Tem certeza que deseja excluir estas ${conversationIdsToDelete.value.length} conversas?`;
  }

  return t('CONVERSATION.DELETE_CONVERSATION.DESCRIPTION');
});

async function deleteConversation() {
  const ids = [...conversationIdsToDelete.value];
  if (!ids.length) return;

  try {
    await Promise.all(ids.map(id => store.dispatch('deleteConversation', id)));
    redirectToConversationList();
    resetBulkActions();
    conversationIdsToDelete.value = [];
    deleteConversationDialogRef.value.close();
    useAlert(
      ids.length > 1
        ? `${ids.length} conversas excluídas com sucesso`
        : t('CONVERSATION.SUCCESS_DELETE_CONVERSATION')
    );
  } catch (error) {
    useAlert(t('CONVERSATION.FAIL_DELETE_CONVERSATION'));
  }
}

const handleDelete = conversationId => {
  const shouldDeleteSelection =
    selectedConversations.value.length > 1 &&
    selectedConversations.value.includes(conversationId);

  conversationIdsToDelete.value = shouldDeleteSelection
    ? [...selectedConversations.value]
    : [conversationId];
  deleteConversationDialogRef.value.open();
};

provide('selectConversation', onSelectConversation);
provide('deSelectConversation', onDeSelectConversation);
provide('assignAgent', onAssignAgent);
provide('assignTeam', onAssignTeam);
provide('assignLabels', onAssignLabels);
provide('updateConversationStatus', toggleConversationStatus);
provide('toggleContextMenu', onContextMenuToggle);
provide('markAsUnread', markAsUnread);
provide('markAsRead', markAsRead);
provide('assignPriority', assignPriority);
provide('isConversationSelected', isConversationSelected);
provide('deleteConversation', handleDelete);

watch(activeTeam, () => resetAndFetchData());

watch(
  computed(() => resolvedConversationInbox.value),
  () => resetAndFetchData()
);
watch(
  computed(() => props.label),
  () => resetAndFetchData()
);
watch(
  computed(() => props.conversationType),
  () => resetAndFetchData()
);

watch(activeFolder, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('customViews/setActiveConversationFolder', newVal || null);
  }
  resetAndFetchData();
});

watch(chatLists, () => {
  chatsOnView.value = conversationList.value;
});

watch(conversationFilters, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('updateChatListFilters', newVal);
  }
});

watch(
  visibleAssigneeTabItems,
  tabs => {
    if (!tabs.length) return;
    if (!tabs.some(tab => tab.key === activeAssigneeTab.value)) {
      updateAssigneeTab(tabs[0].key);
    }
  },
  { immediate: true }
);

watch(
  [isSharedQueueInbox, resolvedConversationInbox],
  ([isSharedQueue]) => {
    if (
      isSharedQueue &&
      activeAssigneeTab.value !== wootConstants.ASSIGNEE_TYPE.ALL
    ) {
      updateAssigneeTab(wootConstants.ASSIGNEE_TYPE.ALL);
    }
  },
  { immediate: true }
);

watch(selectedConversations, selectedIds => {
  if (!selectedIds.length) {
    lastBulkSelectionAnchorId.value = null;
  }
});

watch(normalizedConversationSearch, value => {
  if (contactSearchDebounceTimer) {
    clearTimeout(contactSearchDebounceTimer);
  }

  if (!value) {
    contactSearchResults.value = [];
    searchedConversationResults.value = [];
    isSearchingContacts.value = false;
    contactSearchFailed.value = false;
    return;
  }

  contactSearchDebounceTimer = setTimeout(() => {
    searchContactsForChatList(conversationSearch.value.trim());
  }, 280);
});
</script>

<template>
  <div
    class="flex flex-col flex-shrink-0 bg-n-solid-1 conversations-list-wrap"
    :class="[
      { hidden: !showConversationList },
      isOnExpandedLayout ? 'basis-full' : 'w-[340px] 2xl:w-[412px]',
    ]"
  >
    <slot />
    <ChatListHeader
      :page-title="pageTitle"
      :has-applied-filters="hasAppliedFilters"
      :has-active-folders="hasActiveFolders"
      :active-status="activeStatus"
      :is-on-expanded-layout="isOnExpandedLayout"
      :conversation-stats="conversationStatsDisplay"
      :is-list-loading="chatListLoading && !conversationList.length"
      @add-folders="onClickOpenAddFoldersModal"
      @delete-folders="onClickOpenDeleteFoldersModal"
      @filters-modal="onToggleAdvanceFiltersModal"
      @reset-filters="resetAndFetchData"
      @basic-filter-change="onBasicFilterChange"
    />

    <TeleportWithDirection
      v-if="showAddFoldersModal"
      to="#saveFilterTeleportTarget"
    >
      <SaveCustomView
        v-model="appliedFilter"
        :custom-views-query="foldersQuery"
        :open-last-saved-item="openLastSavedItemInFolder"
        @close="onCloseAddFoldersModal"
      />
    </TeleportWithDirection>

    <DeleteCustomViews
      v-if="showDeleteFoldersModal"
      v-model:show="showDeleteFoldersModal"
      :active-custom-view="activeFolder"
      :custom-views-id="foldersId"
      :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
      @close="onCloseDeleteFoldersModal"
    />

    <ChatTypeTabs
      v-if="shouldShowAssigneeTabs"
      :items="visibleAssigneeTabItems"
      :active-tab="activeAssigneeTab"
      is-compact
      @chat-tab-change="updateAssigneeTab"
      @chat-tab-context-menu="onAssigneeTabContextMenu"
    />

    <div
      v-if="assigneeTabContextMenuVisible"
      class="fixed z-50 overflow-hidden rounded-lg border border-n-weak bg-n-solid-2 shadow-xl min-w-[220px] py-1"
      :style="assigneeTabContextMenuStyle"
      @click.stop
      @contextmenu.prevent
    >
      <button
        type="button"
        class="w-full px-3 py-2 text-sm text-left transition-colors text-n-slate-12 hover:bg-n-alpha-2"
        @click="hideSelectedAssigneeTab"
      >
        Ocultar
        <span class="font-medium">{{ assigneeTabContextMenuItemName }}</span>
      </button>
      <button
        type="button"
        class="w-full px-3 py-2 text-sm text-left transition-colors text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-40 disabled:cursor-not-allowed"
        :disabled="!hiddenAssigneeTabKeys.length"
        @click="showAllAssigneeTabs"
      >
        Mostrar todas
      </button>
    </div>

    <div class="px-3 py-2 border-b border-n-weak">
      <div class="relative flex items-center gap-2">
        <input
          v-model.trim="conversationSearch"
          type="text"
          class="w-full px-3 py-2 text-sm rounded-lg bg-n-solid-2 text-n-slate-12 border border-n-weak focus:outline-none focus:ring-2 focus:ring-n-brand"
          placeholder="Pesquisar conversas ou contato..."
        />
      </div>
      <div
        v-if="hasConversationSearch"
        class="mt-2 border rounded-lg border-n-weak bg-n-solid-2 max-h-48 overflow-y-auto"
      >
        <p v-if="isSearchingContacts" class="px-3 py-2 text-xs text-n-slate-11">
          Pesquisando conversas e mensagens...
        </p>
        <button
          v-for="item in searchedConversationResults"
          :key="item.conversation.id"
          type="button"
          class="w-full px-3 py-2 text-left border-b last:border-b-0 border-n-weak hover:bg-n-alpha-2 transition-colors"
          :style="contactSearchItemStyle(item)"
          @click="onContactSearchClick(item)"
        >
          <div class="flex items-start justify-between gap-3">
            <p class="min-w-0 text-sm font-medium text-n-slate-12 truncate">
              {{ contactSearchLabel(item) }}
            </p>
            <span
              v-if="contactSearchDateLabel(item)"
              class="shrink-0 text-[11px] text-n-slate-11"
            >
              {{ contactSearchDateLabel(item) }}
            </span>
          </div>
          <div class="mt-0.5 text-xs text-n-slate-12">
            <MessagePreview
              v-if="getSearchResultPreviewMessage(item)"
              :message="getSearchResultPreviewMessage(item)"
              :show-message-type="false"
              default-empty-message="Sem conteúdo"
            />
            <p v-else class="truncate text-n-slate-11">
              Sem conteúdo recente
            </p>
          </div>
        </button>
        <p
          v-if="
            !isSearchingContacts &&
            !searchedConversationResults.length &&
            !contactSearchFailed
          "
          class="px-3 py-2 text-xs text-n-slate-11"
        >
          Nenhuma conversa encontrada.
        </p>
        <p
          v-if="contactSearchFailed"
          class="px-3 py-2 text-xs text-ruby-09"
        >
          Falha ao pesquisar contatos.
        </p>
      </div>
    </div>

    <p
      v-if="showConversationEmptyState"
      class="flex items-center justify-center p-4 overflow-auto"
    >
      {{ $t('CHAT_LIST.LIST.404') }}
    </p>
    <div
      v-if="showConversationSearchNoResults"
      class="px-4 pb-3 text-xs text-n-slate-11"
    >
      Nenhuma conversa encontrada para esse termo.
    </div>
    <div
      v-if="selectedConversations.length"
      class="p-3 border-b border-solid border-n-strong dark:border-n-weak"
    >
      <label class="flex items-center justify-center cursor-pointer">
        <input
          type="checkbox"
          class="!m-0 cursor-pointer"
          :checked="allConversationsSelected"
          :indeterminate.prop="!allConversationsSelected"
          @change="toggleSelectAll($event.target.checked)"
        />
        <span class="text-xs my-0 mx-1">
          {{
            $t('BULK_ACTION.CONVERSATIONS_SELECTED', {
              conversationCount: selectedConversations.length,
            })
          }}
        </span>
      </label>
    </div>
    <div
      ref="conversationListRef"
      class="flex-1 overflow-hidden conversations-list hover:overflow-y-auto"
      :class="{ 'overflow-hidden': isContextMenuOpen }"
    >
      <DynamicScroller
        ref="conversationDynamicScroller"
        :items="filteredConversationList"
        :min-item-size="24"
        class="w-full h-full overflow-auto"
      >
        <template #default="{ item, index, active }">
          <!--
            If we encounter resizing issues, we can set the `watchData` prop to true
            this will deeply watch the entire object instead of just size dependencies
            But it can impact performance
          -->
          <DynamicScrollerItem
            :item="item"
            :active="active"
            :data-index="index"
            :size-dependencies="[
              item.messages,
              item.labels,
              item.uuid,
              item.inbox_id,
            ]"
          >
            <ConversationItem
              :source="item"
              :label="label"
              :team-id="teamId"
              :folders-id="foldersId"
              :conversation-type="conversationType"
              :show-assignee="showAssigneeInConversationCard"
              @select-conversation="onSelectConversation"
              @de-select-conversation="onDeSelectConversation"
            />
          </DynamicScrollerItem>
        </template>
        <template #after>
          <div v-if="chatListLoading" class="flex justify-center my-4">
            <Spinner class="text-n-brand" />
          </div>
          <p
            v-else-if="showEndOfListMessage"
            class="p-4 text-center text-n-slate-11"
          >
            {{ $t('CHAT_LIST.EOF') }}
          </p>
          <IntersectionObserver
            v-else
            :options="intersectionObserverOptions"
            @observed="loadMoreConversations"
          />
        </template>
      </DynamicScroller>
    </div>
    <Dialog
      ref="deleteConversationDialogRef"
      type="alert"
      :title="deleteConversationTitle"
      :description="deleteConversationDescription"
      :confirm-button-label="$t('CONVERSATION.DELETE_CONVERSATION.CONFIRM')"
      @confirm="deleteConversation"
      @close="conversationIdsToDelete = []"
    />
    <TeleportWithDirection
      v-if="showAdvancedFilters"
      to="#conversationFilterTeleportTarget"
    >
      <ConversationFilter
        v-model="appliedFilter"
        :folder-name="activeFolderName"
        :is-folder-view="hasActiveFolders"
        @apply-filter="onApplyFilter"
        @update-folder="onUpdateSavedFilter"
        @close="closeAdvanceFiltersModal"
      />
    </TeleportWithDirection>
  </div>
</template>
